<template>
    <Layout :localeData="localeData">
        <main :class="$style.main">
            <Breadcrumbs :items="pathCategories" />
            <div :class="$style.container">
                <CategoryHeader :category="categoryInfo" />
                <div :class="$style.pagesAndSeo">
                    <SeoTextExtendable
                        v-if="categoryInfo?.seoText && currentPageIndex === 1"
                        :content="categoryInfo.seoText"
                    />
                    <div :class="$style.pages">
                        <div
                            v-if="Array.isArray(pages[pageIndex]) || showPlaceholders"
                            :id="`page-${currentPageIndex}`"
                            :data-page-number="currentPageIndex"
                        >
                            <div :class="$style.grid">
                                <template v-if="Array.isArray(pages[pageIndex])">
                                    <template
                                        v-for="(game, cardIndex) in pages[pageIndex]"
                                        :key="game.hru"
                                    >
                                        <template v-if="cardIndex === BIG_CARD_INDEX">
                                            <GameCard
                                                :class="[$style.desktop_override, $style.bigCard_override]"
                                                data-interface-target="game"
                                                :game="game"
                                                appearance="big_horizontal"
                                                :approximateWidth="280 * 2"
                                                preload
                                                responsive
                                                :lazy="false"
                                                withBadges
                                                :targetLink="gameLinkTarget"
                                            />
                                            <GameCard
                                                :class="[$style.mobile_override, $style.bigCard_override]"
                                                data-interface-target="game"
                                                :game="game"
                                                appearance="big_horizontal"
                                                :approximateWidth="280"
                                                responsive
                                                lazy
                                                withBadges
                                                :targetLink="gameLinkTarget"
                                            />
                                        </template>
                                        <GameCard
                                            v-else
                                            data-interface-target="game"
                                            :game="game"
                                            appearance="medium_horizontal"
                                            :approximateWidth="280"
                                            responsive
                                            :lazy="cardIndex > 2"
                                            withBadges
                                            :targetLink="gameLinkTarget"
                                        />
                                    </template>
                                </template>
                                <template v-else>
                                    <template
                                        v-for="(_, idx) in placeholderPage"
                                        :key="`placeholder-${idx}`"
                                    >
                                        <template v-if="idx === BIG_CARD_INDEX">
                                            <GameCard
                                                :class="$style.bigCard_override"
                                                :game="undefined"
                                                appearance="big_horizontal"
                                                responsive
                                                withBadges
                                            />
                                        </template>
                                        <GameCard
                                            v-else
                                            :game="undefined"
                                            appearance="big_horizontal"
                                            responsive
                                            withBadges
                                        />
                                    </template>
                                </template>
                            </div>
                        </div>
                        <CategoryPageToggle
                            v-if="category?.totalPages && category.totalPages > 1"
                            :currentPage="currentPageIndex"
                            :totalPages="category.totalPages"
                            :numShow="5"
                            :getPageLink="getPageLink"
                        />
                    </div>
                </div>
                <PromoCategories :class="$style.promo_override" />
                <TheEnd />
            </div>
        </main>
    </Layout>
</template>
<script setup lang="ts">
import { ReactiveHead, useHead } from '@unhead/vue'

import { storeToRefs } from 'pinia'
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Breadcrumbs from '@/components_new/Breadcrumbs.vue'
import GameCard from '@/components_new/GameCard/GameCard.vue'
import PromoCategories from '@/components_new/PromoCategories/PromoCategories.vue'
import SeoTextExtendable from '@/components_new/SeoText/SeoTextExtendable.vue'
import TheEnd from '@/components_new/TheEnd/TheEnd.vue'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { useUrl } from '@/composables/url-helpers'
import { SCHEMA_CURRENCY, useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import Layout from '@/layouts/Layout.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { formatValue, mapDigitToUnicode } from '@/utils/numbers'
import { useSetPageData } from '@/utils/vue-hooks/use-set-page-data'

import { useCategoryPageStore } from './category-page-store'
import CategoryHeader from './components/CategoryHeader.vue'
import CategoryPageToggle from './components/CategoryPageToggle.vue'

const BIG_CARD_INDEX = 0
const TOP_GAMES_COUNT = 20

const categoryPageStore = useCategoryPageStore()
const appStore = useAppStore()
const adsStore = useAdsStore()

function getPageLink(page: number) {
    return {
        name: pageType.value,
        ...(page > 1 ? { query: { page } } : {}),
    }
}

const tr = useTr()

const { platform } = storeToRefs(appStore)
const { pages, pageSize, category, categoryInfo } = storeToRefs(categoryPageStore)
const currentPageIndex = computed(() => ($route.query.page ? parseInt($route.query.page as string, 10) : 1))
const pageIndex = computed(() => currentPageIndex.value - 1)
const placeholderPage = computed(() => Array(pageSize.value).fill(null))
const $route = useRoute()
const router = useRouter()
const { isPartOf, getFullLink, getInLanguage } = useSchemaOrg()
const { getCategoryRoute } = useUrl()

const categoryHru = computed(() => $route.params.hru as string)
const gameLinkTarget = computed(() => (platform.value === 'desktop' ? '_blank' : undefined))

const pageType = computed(() => {
    switch ($route.name) {
        case ROUTE_NAMES.TAG:
            return 'tag'
        case ROUTE_NAMES.SERIES:
            return 'series'
        default:
            return 'category'
    }
})

const pathCategories = computed(() => {
    const catPath = categoryInfo.value?.pathCategories || []
    const path = catPath.map((p, index) => ({
        ...p,
        disabled: index === catPath.length - 1,
    }))
    if (path) {
        return [
            {
                title: tr('categoryPage.main_title'),
                href: router.resolve({ name: ROUTE_NAMES.MAIN }).fullPath,
            },
            ...path,
        ]
    }
    return []
})

const fullTitle = computed(() => categoryInfo.value?.fullTitle || '')

const unicodeTotalGames = computed(() =>
    mapDigitToUnicode(formatValue(category.value?.total || 0, { minSeparated: 1000 })),
)

const defaultMetaTitle = computed(() => {
    switch (pageType.value) {
        case 'tag':
            return tr('categoryPage.tags_title', { title: fullTitle.value })
        case ROUTE_NAMES.SERIES:
            return tr('categoryPage.series_title', { title: fullTitle.value })
        default:
            return tr('categoryPage.title', { title: fullTitle.value })
    }
})

const defaultMetaDescription = computed(() => {
    switch (pageType.value) {
        case 'tag':
            return tr('categoryPage.tags_description', { title: fullTitle.value })
        case ROUTE_NAMES.SERIES:
            return tr('categoryPage.series_description', { title: fullTitle.value })
        default:
            return tr('categoryPage.description', { title: fullTitle.value })
    }
})

const metaTitle = computed(
    () =>
        `${categoryInfo.value?.metaTitle || defaultMetaTitle.value} ${tr('categoryPage.total', { total: unicodeTotalGames.value })}`,
)
const metaDescription = computed(() => categoryInfo.value?.metaDescription || defaultMetaDescription.value)

function routeChanged() {
    const mainRequest = categoryPageStore.setCategory({
        categoryName: categoryHru.value,
        pageType: pageType.value,
        page: currentPageIndex.value,
    })

    return mainRequest
}

const pageStr = computed(() =>
    currentPageIndex.value > 1 ? ` | ${tr('categoryPage.page', { page: String(currentPageIndex.value) })}` : '',
)

const breadcrumbsSchema = computed(() => {
    if (!pathCategories.value.length) {
        return {}
    }

    const breadcrumbItems = pathCategories.value.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        item: getFullLink(item.href),
    }))

    return {
        breadcrumb: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
        },
    }
})

const mainEntity = computed(() => {
    const currentPageGames = pages.value[pageIndex.value]?.slice(0, TOP_GAMES_COUNT) || []

    if (!currentPageGames.length || currentPageIndex.value > 1) {
        return {}
    }

    const gameItems = currentPageGames.map((game, index) => ({
        '@type': 'ListItem',
        position: 1 + index,
        name: game.title,
        url: getFullLink({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.hru } }),
    }))

    return {
        mainEntity: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: fullTitle.value,
            numberOfItems: currentPageGames.length,
            itemListElement: gameItems,
        },
    }
})

const organizationSchema = computed(() => {
    if (!categoryInfo.value) {
        return {}
    }
    const [firstWordOfTitle] = fullTitle.value.split(' ')
    const getCategoryCharCode = (word: string, index?: number) => {
        return word.charCodeAt(index ?? word.length - 1)
    }
    const categoryCharCode = getCategoryCharCode(firstWordOfTitle)
    // Rating by last letter of category first word title
    const getRating = () => (categoryCharCode % 4) / 10 + 4.2
    // RatingCount by last letter of category first word title
    const getRatingCount = () => {
        const timestamp = new Date().getTime()
        const msInDay = 86400000
        // take current dayNumber
        const dayNumber = Math.floor(timestamp / msInDay)
        // take getCategoryCharCode and multiple by number from categoryCharCode or 90 if categoryCharCode is 0
        let total = categoryCharCode * getCategoryCharCode(firstWordOfTitle, 0)
        // add everyday 4 new points more
        total += dayNumber * 4
        // got from ~ 88_000 to 91_000 points that changes every day by 4 points
        return total
    }
    const aggregateRating = categoryCharCode
        ? {
              '@type': 'AggregateRating',
              bestRating: 5,
              ratingValue: getRating(),
              ratingCount: getRatingCount(),
              worstRating: 1,
          }
        : {}
    const currentRoute = getFullLink(router.currentRoute.value.path)
    const currentRouteWithPagination = getFullLink({
        name: pageType.value,
        query: { page: currentPageIndex.value > 1 ? currentPageIndex.value : undefined },
    })
    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'CollectionPage',
                '@id': `${currentRouteWithPagination}#CollectionPage`,
                url: currentRouteWithPagination,
                name: `${metaTitle.value}${pageStr.value}`,
                description: `${metaDescription.value}${pageStr.value}`,
                inLanguage: getInLanguage(),
                ...breadcrumbsSchema.value,
                ...mainEntity.value,
                ...isPartOf.value,
            },

            {
                '@type': 'WebApplication',
                '@id': `${currentRoute}#WebApplication`,
                url: currentRoute, // without ?page
                applicationCategory: 'GameApplication',
                name: categoryInfo.value.fullTitle, // without pageStr
                description: metaDescription.value,
                operatingSystem: ['Windows', 'Chrome OS', 'Linux', 'MacOS', 'Android', 'iOS'],
                availableOnDevice: ['Mobile', 'Tablet', 'Desktop'],
                aggregateRating,
                offers: {
                    '@type': 'Offer',
                    price: 0,
                    priceCurrency: SCHEMA_CURRENCY,
                    availability: 'http://schema.org/InStock',
                },
                isPartOf: { '@id': `${currentRoute}#CollectionPage` },
            },
        ],
    }
})

const { getCanonicalAndAlternates, getLocalizedUrls } = useLocalizedRouters()

const pageMeta = computed(() => {
    if (!category.value || !category.value.id || !categoryInfo.value) {
        return {}
    }

    const links: ReactiveHead['link'] = [
        ...getCanonicalAndAlternates({
            mapRoute(route, locale) {
                const localizedHru = categoryInfo.value?.localizations?.find((l) => l.lang === locale)?.hru
                if (!localizedHru) {
                    return route
                }
                return {
                    ...route,
                    params: {
                        ...route.params,
                        hru: localizedHru,
                    },
                    query: {
                        page: route.query?.page,
                    },
                }
            },
        }),
    ]

    if (currentPageIndex.value > 1) {
        links.push({
            rel: 'prev',
            href: router.resolve({
                ...getCategoryRoute({ hru: categoryHru.value, type: category.value.categoryType }),
                query: { page: currentPageIndex.value - 1 },
            }).href,
        })
    }
    if (currentPageIndex.value < category.value.totalPages) {
        links.push({
            rel: 'next',
            href: router.resolve({
                ...getCategoryRoute({ hru: categoryHru.value, type: category.value.categoryType }),
                query: { page: currentPageIndex.value + 1 },
            }).href,
        })
    }

    return {
        title: () => `${metaTitle.value}${pageStr.value}`,
        script: [
            {
                type: 'application/ld+json',
                innerHTML: organizationSchema.value,
            },
        ],
        meta: [
            {
                name: 'title',
                content: `${metaTitle.value}${pageStr.value}`,
            },
            {
                name: 'description',
                content: `${metaDescription.value}${pageStr.value}`,
            },
            {
                property: 'og:title',
                content: `${metaTitle.value}${pageStr.value}`,
            },
            {
                property: 'og:description',
                content: `${metaDescription.value}${pageStr.value}`,
            },
            {
                name: 'twitter:title',
                content: `${metaTitle.value}${pageStr.value}`,
            },
            {
                name: 'twitter:description',
                content: `${metaDescription.value}${pageStr.value}`,
            },
        ],
        link: links,
    }
})

const localeData = computed(() => {
    return {
        languageLinks: getLocalizedUrls({
            mapRoute(route, locale) {
                const localizedHru = categoryInfo.value?.localizations?.find((l) => l.lang === locale)?.hru

                return localizedHru
                    ? {
                          ...route,
                          params: {
                              ...route.params,
                              hru: localizedHru,
                          },
                          query: {
                              page: route.query?.page,
                          },
                      }
                    : route
            },
        }),
    }
})

useHead(pageMeta)

watch(
    () => categoryHru.value,
    () => routeChanged(),
)

watch(
    () => currentPageIndex.value,
    () => routeChanged(),
)

watch(
    () => categoryInfo.value?.id,
    () => {
        adsStore.updateTargeting({ content_category: categoryInfo.value?.id || '' })
    },
)

// TODO deduplicate with Game.vue
function processRedirectResponse() {
    const { categoryRedirect } = categoryPageStore
    if (!categoryRedirect) {
        return
    }
    const { redirectTarget, status: statusCode } = categoryRedirect

    let redirectURL = ''

    if (redirectTarget.type === 'game') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: redirectTarget.game_hru },
            query: router.currentRoute.value.query,
        }).href
    } else if (redirectTarget.type === 'category') {
        redirectURL = router.resolve({
            ...getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
            query: router.currentRoute.value.query,
        }).href
    } else if (redirectTarget.type === 'main-page') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.MAIN,
            query: router.currentRoute.value.query,
        }).href
    }

    appStore.setHeaders({
        'X-BFF-Redirect-Mechanism': redirectTarget.mechanism,
        'X-BFF-Redirect-Reason': redirectTarget.reason,
    })
    appStore.setPageStatus(statusCode)
    appStore.setTargetURL(redirectURL)
}

onServerPrefetch(async () => {
    await routeChanged()
    processRedirectResponse()
})

const showPlaceholders = ref(false)

useSetPageData(() => ({
    content_category: pageType.value === 'category' ? categoryInfo.value?.id || '' : '',
}))

onMounted(() => {
    showPlaceholders.value = true
    if (category.value?.id !== categoryHru.value) {
        routeChanged()
    }
    processRedirectResponse()
})
</script>
<style module>
.container {
    --pages-gap: 8px;
}

.bigCard_override {
    grid-column: span 2;
    grid-row: span 2;
}

.container,
.main,
.pagesAndSeo,
.pages {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.pagesAndSeo {
    margin-left: -3px;
}

/*
    Style for empty Ad block.
    Cannot not be just "display: none" because Ad container needs to be visible to react on size change.
*/
.emptyAd {
    margin-top: calc(var(--pages-gap) * -1);
}

.pagesAndSeo,
.main {
    flex-direction: column-reverse;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    --cards-gap: 8; /* unitless, consumed by GameCard component */
    row-gap: 3px;
    column-gap: 8px;
    position: relative;
}

.desktop_override {
    display: none;
}

.promo_override {
    margin: calc(24px - var(--pages-gap)) 0;
}

@media (--mobile-landscape) {
    .grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (--tablet-plus) {
    .grid {
        grid-template-columns: repeat(4, 1fr);
    }

    .desktop_override {
        display: block;
    }

    .mobile_override {
        display: none;
    }
}

@media (--desktop-medium-plus) {
    .grid {
        grid-template-columns: repeat(6, 1fr);
    }
}
</style>
